import { useRequestVerificationEmailMutation, useSendVerificationCodeMutation } from 'services/users';
import { Input } from 'ui/atoms/inputs';
import { Button } from 'ui/atoms/buttons';
import { Heading, Subheading } from 'ui/typography/Heading'
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const DEBOUNCE_DELAY = 60000; // 60 seconds in milliseconds

export const VerifyRealmPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
`;

export const VerifyRealmForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 300px;
`;

export const StyledInput = styled(Input)`
  margin-bottom: 12px;
`;

export const ErrorMessage = styled.p`
  color: #d32f2f;
  font-size: 14px;
  margin-top: 8px;
`;

export const VerifyRealmPage = () => {
  const [requestVerificationEmail, { isLoading: isRequestLoading }] = useRequestVerificationEmailMutation();
  const [sendVerificationCode, { isLoading, isSuccess }] = useSendVerificationCodeMutation();

  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [codeRequested, setCodeRequested] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [emailCooldownTime, setEmailCooldownTime] = useState<number>(0);
  const emailTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleRequestEmail = async () => {
    try {
      await requestVerificationEmail().unwrap();
      setCodeRequested(true);
      setEmailCooldownTime(DEBOUNCE_DELAY);
      toast.success(t('requestEmailSuccessToast') as string);

      // Start countdown timer
      if (emailTimerRef.current) clearInterval(emailTimerRef.current);
      emailTimerRef.current = setInterval(() => {
        setEmailCooldownTime(prev => {
          const newTime = prev - 1000;
          if (newTime <= 0) {
            if (emailTimerRef.current) clearInterval(emailTimerRef.current);
            return 0;
          }
          return newTime;
        });
      }, 1000);
    } catch (error) {
      toast.error(t('requestEmailErrorToast') as string);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);
    const errors: Record<string, string> = {};

    const verification_code = formData.get('verification_code') as string;

    if (!verification_code) errors.verification_code = t('verificationCodeRequired') as string;
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      await sendVerificationCode({ code: verification_code }).unwrap();
    } catch (error) {
      setValidationErrors({ submit: t('invalidVerificationCode') as string });
    }
  };

  useEffect(() => {
    return () => {
      if (emailTimerRef.current) clearInterval(emailTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (emailCooldownTime === 0 && emailTimerRef.current) {
      clearInterval(emailTimerRef.current);
    }
  }, [emailCooldownTime]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(t('verificationSuccessToast') as string);
      navigate('/backoffice/');
    }
  }, [isSuccess, navigate, t]);
    

  const isEmailDisabled = isRequestLoading || emailCooldownTime > 0;

  return (
    <VerifyRealmPageContainer>
      <Heading>{t('heading')}</Heading>
      <Subheading>{t('subheading')}</Subheading>

      {!codeRequested ? (
        <Button onClick={handleRequestEmail} disabled={isEmailDisabled}>
          {isRequestLoading ? t('requestEmailButtonLoading') : t('requestEmailButton')}
        </Button>
      ) : (
        <VerifyRealmForm onSubmit={handleSubmit}>
          <StyledInput
            type="text"
            name="verification_code"
            placeholder={t('verificationCodePlaceholder') as string}
          />
          {validationErrors.verification_code && <ErrorMessage>{validationErrors.verification_code}</ErrorMessage>}
          {validationErrors.submit && <ErrorMessage>{validationErrors.submit}</ErrorMessage>}
          <Button type="submit" disabled={isLoading}>
            {isLoading ? t('verifyingButtonLoading') : t('verifyButton')}
          </Button>
        </VerifyRealmForm>
      )}
    </VerifyRealmPageContainer>
  );
}
